'use client';

import * as React from 'react';
import ReactDOM from 'react-dom';
import Script from 'next/script';
import type { schema } from 'database';

export type LayoutThemeLoaderProps = {
  cssPromise: Promise<string>;
  fontSize?: number | null;
  customJs?: string | null;
  cssVariables?: schema.AllCssVariables | null;
};

export function LayoutThemeLoader({
  fontSize,
  cssPromise,
  customJs,
  cssVariables,
}: LayoutThemeLoaderProps): JSX.Element {
  ReactDOM.preconnect('https://fonts.gstatic.com');
  ReactDOM.preconnect('https://static.quotion.net');

  let fontStyle = '';
  if (fontSize) {
    fontStyle = `html { font-size: ${fontSize}px; }`;
  }
  const css = React.use(cssPromise);
  const finalCss = `
    ${fontStyle}

    body {
      ${Object.entries(cssVariables ?? {})
        .map(([key, value]) => `${key}: ${value};`)
        .join('\n')}
    }

    ${css}
  `;

  return (
    <ResourcesLoader
      cssId="layout-css"
      css={finalCss}
      js={customJs}
      jsId="layout-js"
    />
  );
}

type ResourcesLoaderProps = {
  css?: string;
  cssId?: string;
  cssPrecedence?: 'high' | 'medium' | 'low';
  js?: string | null;
  jsId: string;
};

export function ResourcesLoader({
  css,
  cssId,
  cssPrecedence = 'high',
  js,
  jsId,
}: ResourcesLoaderProps) {
  React.useEffect(() => {
    if (!css || !cssId) {
      return;
    }
    // React converts style href to data-href
    const theStyle = document.querySelector(`style[data-href="${cssId}"]`);
    if (!theStyle) {
      const style = document.createElement('style');
      style.setAttribute('data-href', cssId);
      style.textContent = css;
      // Fix style missing when navigating between pages
      document.head.appendChild(style);
    }

    // Clean up styles when component unmounts or re-renders
    return () => {
      const existingStyle = document.querySelector(
        `style[data-href="${cssId}"]`,
      );
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [css, cssId]);

  return (
    <>
      {css && (
        // Procedence and href make sure the style is rendered in html head
        <style key={cssId} precedence={cssPrecedence} href={cssId}>
          {css}
        </style>
      )}
      {js && <Script id={jsId}>{js}</Script>}
    </>
  );
}
